import { For, Match, Show, Switch, createMemo } from 'solid-js'

import HistoryTimeBlocks from './HistoryTimeBlocks'
import { dispatchAppAction } from '../../runtimeBridge'
import { useI18n } from '../../i18n'
import { historySummary } from '../../state/historySummaryStore'
import { FAT_PER_CHOCOLATE_GRAMS, getChocolateBars } from '../../utils/chocolateBars'

/**
 * Renders the history summary period selector buttons.
 *
 * @returns The selector element.
 */
const HistoryPeriodSelector = () => (
   <Show when={historySummary().periodOptions.length > 0}>
      <div class="inline-flex items-center gap-1 rounded-lg border border-emerald-300/60 bg-emerald-500/10 p-0.5">
         <For each={historySummary().periodOptions}>
            {option => (
               <button
                  type="button"
                  data-history-period={option.key}
                  onClick={() =>
                     dispatchAppAction({
                        action: 'select-history-period',
                        periodKey: option.key,
                     })
                  }
                  aria-pressed={option.active ? 'true' : 'false'}
                  class={`history-summary-period-btn px-2.5 py-1 text-xs font-semibold rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60 ${
                     option.active ?
                        'bg-emerald-400 text-slate-900 shadow-sm'
                     :  'text-emerald-100/80 hover:bg-emerald-500/20'
                  }`}
               >
                  {option.shortLabel}
               </button>
            )}
         </For>
      </div>
   </Show>
)

/**
 * Renders the chocolate-bar reward for the period's fat burned.
 *
 * @param props - Fat burned in grams.
 * @returns The reward element (empty until at least some fat is burned).
 */
const ChocolateReward = (props: { totalGrams: number }) => {
   const t = useI18n()
   const bars = createMemo(() => getChocolateBars(props.totalGrams))
   const iconBars = createMemo(() => {
      const current = bars()
      return current.type === 'icons' ? current : null
   })
   return (
      <Switch>
         <Match when={bars().type === 'message'}>
            <div class="mt-1 flex items-center gap-2 text-sm text-emerald-100/80">
               <span class="text-2xl">🔥</span>
               <span>
                  {t('First bar on the way! 🍫 = {grams} g', {
                     grams: FAT_PER_CHOCOLATE_GRAMS,
                  })}
               </span>
            </div>
         </Match>
         <Match when={iconBars()}>
            {icons => (
               <div
                  class="mt-1 flex flex-wrap items-center gap-1 text-3xl leading-none"
                  aria-label={t('{count} chocolate bars worth of fat burned', {
                     count: icons().count,
                  })}
               >
                  <span>{'🍫'.repeat(icons().count)}</span>
                  <Show when={icons().overflow > 0}>
                     <span class="text-sm font-semibold text-emerald-200">
                        +{icons().overflow}
                     </span>
                  </Show>
                  <span class="text-2xl">🔥</span>
               </div>
            )}
         </Match>
      </Switch>
   )
}

/**
 * Renders the history summary card (period totals and fat burned).
 *
 * @returns The summary element.
 */
const HistorySummary = () => {
   const t = useI18n()
   const badgeLabel = createMemo(() =>
      t('fast count badge', { count: historySummary().badgeCount }),
   )

   return (
      <div
         id="history-summary"
         class="mb-4 flex flex-col gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100 shadow-lg shadow-emerald-900/40"
         onPointerDown={() => dispatchAppAction({ action: 'freeze-history-summary' })}
         onKeyDown={event => {
            if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
               dispatchAppAction({ action: 'freeze-history-summary' })
            }
         }}
      >
         <Switch>
            <Match when={historySummary().state === 'empty'}>
               <span class="text-xs uppercase tracking-wider text-emerald-200">
                  {historySummary().label}
               </span>
               <span class="text-sm text-emerald-100/80">
                  {historySummary().emptyMessage}
               </span>
            </Match>
            <Match when={historySummary().state === 'no-fasts'}>
               <div class="flex flex-wrap items-center justify-between gap-2">
                  <p class="text-xs uppercase tracking-wider text-emerald-200">
                     {historySummary().label}
                  </p>
                  <HistoryPeriodSelector />
               </div>
               <p class="text-sm text-emerald-100/80">{historySummary().emptyMessage}</p>
            </Match>
            <Match when={historySummary().state === 'summary'}>
               <div class="flex flex-wrap items-center justify-between gap-2">
                  <div class="flex items-center gap-2">
                     <p class="text-xs uppercase tracking-wider text-emerald-200">
                        {historySummary().label}
                     </p>
                     <span class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border border-emerald-300/70 bg-emerald-500/20 text-emerald-100/90">
                        {badgeLabel()}
                     </span>
                  </div>
                  <HistoryPeriodSelector />
               </div>
               <p class="text-3xl font-black text-emerald-200 leading-tight">
                  {historySummary().formattedTotal}
               </p>
               <p class="text-sm text-emerald-100/80">{historySummary().description}</p>
               <ChocolateReward totalGrams={historySummary().totalGrams} />
            </Match>
         </Switch>
         <HistoryTimeBlocks />
      </div>
   )
}

export default HistorySummary
